import React, { useRef, useState, useEffect } from "react";
import styles from "./SendEmailModal.module.scss";
import classNames from "classnames/bind";
import BaseInputV2 from "~/components/common/base-input-v2/BaseInputV2";
import gmailIntegrationApi, { IGmailIntegration } from "~/apis/gmailIntegration";
import { notify } from "~/utils/helper";
const cx = classNames.bind(styles);

interface SendEmailModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSuccess?: () => void;
  customerEmail?: string;
  customerName?: string;
  jobId?: string;
}

const SendEmailModal: React.FC<SendEmailModalProps> = ({
  isOpen,
  onCancel,
  onSuccess,
  customerEmail,
  customerName,
  jobId,
}) => {
  const [formData, setFormData] = useState({
    subject: "",
    content: "",
    file: null as File | null,
  });
  const [error, setError] = useState({
    subject: "",
    content: "",
  });
  const [isDragActive, setIsDragActive] = useState(false);
  const [sending, setSending] = useState(false);
  const [integrations, setIntegrations] = useState<IGmailIntegration[]>([]);
  const [checkingIntegration, setCheckingIntegration] = useState(true);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      fetchIntegrations();
      // Reset form when modal opens
      setFormData({
        subject: "",
        content: "",
        file: null,
      });
      setError({
        subject: "",
        content: "",
      });
    }
  }, [isOpen]);

  const fetchIntegrations = async () => {
    setCheckingIntegration(true);
    try {
      const response = await gmailIntegrationApi.getIntegrations();
      if (response.data?.data) {
        setIntegrations(response.data.data);
      }
    } catch (error: any) {
      console.error("Failed to fetch Gmail integrations:", error);
    } finally {
      setCheckingIntegration(false);
    }
  };

  const formatFileSize = (size: number) => {
    if (size >= 1024 ** 3) return `${(size / 1024 ** 3).toFixed(2)} GB`;
    if (size >= 1024 ** 2) return `${(size / 1024 ** 2).toFixed(2)} MB`;
    if (size >= 1024) return `${(size / 1024).toFixed(2)} KB`;
    return `${size} B`;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 25 * 1024 * 1024) {
        // 25MB limit for email attachments
        notify("error", "File size must be less than 25MB");
        return;
      }
      setFormData({
        ...formData,
        file,
      });
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      if (file.size > 25 * 1024 * 1024) {
        notify("error", "File size must be less than 25MB");
        return;
      }
      setFormData({
        ...formData,
        file,
      });
    }
  };

  const handleRemoveFile = () => {
    setFormData({
      ...formData,
      file: null,
    });
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const openFileDialog = () => {
    inputRef.current?.click();
  };

  const validateForm = (): boolean => {
    let hasError = false;
    const newError = {
      subject: "",
      content: "",
    };

    if (!formData.subject.trim()) {
      newError.subject = "Email subject is required.";
      hasError = true;
    }

    if (!formData.content.trim()) {
      newError.content = "Email content is required.";
      hasError = true;
    }

    setError(newError);
    return !hasError;
  };

  // Convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = (reader.result as string).split(',')[1]; // Remove data:type;base64, prefix
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  // Get MIME type from file
  const getMimeType = (file: File): string => {
    return file.type || 'application/octet-stream';
  };

  const handleSend = async () => {
    if (!validateForm()) {
      return;
    }

    const connectedIntegration = integrations.find(
      (integration) => integration.status === "connected" && integration.provider === "google"
    );

    if (!connectedIntegration) {
      notify("error", "Gmail is not connected. Please connect Gmail in Settings first.");
      return;
    }

    if (!customerEmail) {
      notify("error", "Customer email is required.");
      return;
    }

    setSending(true);
    try {
      // Prepare attachments if file exists
      const attachments = formData.file
        ? [
            {
              filename: formData.file.name,
              content: await fileToBase64(formData.file),
              mimeType: getMimeType(formData.file),
            },
          ]
        : undefined;

      await gmailIntegrationApi.sendEmail({
        to: customerEmail,
        subject: formData.subject,
        body: formData.content,
        attachments,
        jobId,
      });

      notify("success", "Email sent successfully!");
      onCancel();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      notify("error", error?.response?.data?.message || "Failed to send email. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const connectedIntegration = integrations.find(
    (integration) => integration.status === "connected" && integration.provider === "google"
  );

  if (!isOpen) return null;

  return (
    <div className={cx("overlay")} onClick={onCancel}>
      <div
        className={cx("send-email-modal-container")}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={cx("header")}>
          <h4 className={cx("header-title")}>Send Email</h4>
          <p className={cx("header-description")}>
            {customerName ? `Send email to ${customerName}` : "Send email to customer"}
          </p>
        </div>

        <div className={cx("content")}>
          {checkingIntegration ? (
            <div className={cx("loading-container")}>
              <p>Checking Gmail connection...</p>
            </div>
          ) : !connectedIntegration ? (
            <div className={cx("warning-container")}>
              <p className={cx("warning-text")}>
                Gmail is not connected. Please connect your Gmail account in Settings to send emails.
              </p>
            </div>
          ) : (
            <>
              <div className={cx("email-info")}>
                <p className={cx("email-label")}>To:</p>
                <p className={cx("email-value")}>{customerEmail || "N/A"}</p>
              </div>

              <BaseInputV2
                type="text"
                label="Email Subject"
                placeholder="Enter email subject"
                value={formData.subject}
                onChange={(e) => {
                  setFormData({ ...formData, subject: e.target.value });
                  if (error.subject) {
                    setError({ ...error, subject: "" });
                  }
                }}
                errorMessage={error.subject}
              />

              <div className={cx("content-field")}>
                <label className={cx("content-label")}>Email Content</label>
                <textarea
                  className={cx("content-textarea", { "error": error.content })}
                  placeholder="Enter email content"
                  value={formData.content}
                  onChange={(e) => {
                    setFormData({ ...formData, content: e.target.value });
                    if (error.content) {
                      setError({ ...error, content: "" });
                    }
                  }}
                  rows={8}
                />
                {error.content && (
                  <p className={cx("error-message")}>{error.content}</p>
                )}
              </div>

              <div className={cx("attachment-section")}>
                <label className={cx("attachment-label")}>File Attachment (Optional)</label>
                <div
                  className={cx("upload-area", {
                    "error-upload": false,
                    "drag-active": isDragActive,
                  })}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  {formData.file ? (
                    <div className={cx("file-preview")}>
                      <p className={cx("file-info")}>
                        {formData.file.name} ({formatFileSize(formData.file.size)})
                      </p>
                      <div className={cx("divider")}></div>
                      <button
                        className={cx("btn-remove")}
                        onClick={handleRemoveFile}
                        type="button"
                      >
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 20 20"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M15 5L5 15M5 5L15 15"
                            stroke="white"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <p>Remove</p>
                      </button>
                    </div>
                  ) : (
                    <div className={cx("upload-placeholder")}>
                      <p className={cx("upload-text")}>
                        Drag and drop your file here or{" "}
                        <span className={cx("click-upload")} onClick={openFileDialog}>
                          Click to upload
                        </span>
                      </p>
                      <p className={cx("upload-description")}>
                        Maximum file size: 25MB
                      </p>
                    </div>
                  )}
                  <input
                    ref={inputRef}
                    type="file"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                  />
                </div>
              </div>
            </>
          )}
        </div>

        <div className={cx("footer")}>
          <button
            className={cx("footer-cancel-button")}
            onClick={onCancel}
            disabled={sending}
          >
            Cancel
          </button>
          <button
            className={cx("footer-save-button")}
            onClick={handleSend}
            disabled={sending || !connectedIntegration || checkingIntegration}
          >
            {sending ? "Sending..." : "Send Email"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SendEmailModal;

